import { useState, useEffect } from 'react';
import { Plus, Tag, Sparkles } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { useServices } from './hooks';
import { TimeWindowEditor } from './TimeWindowEditor';
import { SlidePanel } from '../../components/common/SlidePanel';
import { MoneyInput } from '../../components/common/MoneyInput';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { canonicalMoney, subtractMoney } from '../../lib/money';
import type { Service, PeakWindow } from '../../types';

const TH: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: admin.caption, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${admin.border}`, whiteSpace: 'nowrap' };
const TD: React.CSSProperties = { fontSize: 13, color: palette.g700, padding: '10px 12px', borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap' };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }

export function ServicesPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const patchBusiness = useAuthStore(s => s.patchBusiness);
  const { services, categories, loading, error, summary, filters, setFilters, createService, updateService, toggleActive } = useServices(business?.id);
  const [editing, setEditing] = useState<Service | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const bizDynamic = business?.enable_dynamic_pricing === true;
  const tier = business?.tier ?? 'free';
  const canUseDynamic = tier === 'pro' || tier === 'business' || tier === 'trial';

  const toggleBizDynamic = async () => {
    if (!business?.id) return;
    const next = !bizDynamic;
    await supabase.from('businesses').update({ enable_dynamic_pricing: next }).eq('id', business.id);
    patchBusiness({ enable_dynamic_pricing: next });
    setToast({ msg: next ? 'Dynamic pricing enabled' : 'Dynamic pricing disabled', type: 'success' });
  };

  useEffect(() => { document.title = 'CrewBit · Services'; }, []);

  if (loading) return <div><Skeleton width={100} height={24} style={{ marginBottom: 24 }} /><div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={5} /></div></div>;
  if (error) return <div><div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Services</div><div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{error}</div></div>;

  const active = services.filter(s => s.is_active !== false);
  const inactive = services.filter(s => s.is_active === false);

  const handleToggle = async (svc: Service) => {
    if (!user) return;
    const ok = await toggleActive(svc.id, svc.is_active !== false, user.id);
    if (ok) setToast({ msg: `Service ${svc.is_active !== false ? 'deactivated' : 'activated'}`, type: 'success' });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Services</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Manage your service offerings, pricing, and durations</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={15} /> Add Service</button>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Active services" value={String(summary.activeCount)} />
        <MetricCard label="Avg price" value={fmt$(summary.avgPrice)} />
        <MetricCard label="Avg duration" value={`${summary.avgDuration}m`} />
        {summary.topBooked.length > 0 && <MetricCard label="Most booked" value={summary.topBooked[0].name} sub={`${summary.topBooked[0].count} bookings`} />}
      </div>

      {/* Dynamic pricing master toggle */}
      {canUseDynamic && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderRadius: admin.cardRadius, marginBottom: 16,
          background: bizDynamic ? palette.accentWash : palette.g50,
          border: `1px solid ${bizDynamic ? palette.accent + '30' : palette.g200}`,
        }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.g800 }}>Dynamic pricing</div>
            <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>Adjust service prices automatically based on demand</div>
          </div>
          <button onClick={toggleBizDynamic} style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: bizDynamic ? palette.accent : palette.g200, position: 'relative', transition: `background ${transition.fast}`,
            flexShrink: 0,
          }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2, left: bizDynamic ? 22 : 2, transition: `left ${transition.fast}`, boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
          </button>
        </div>
      )}

      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Search services..."
            style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 220 }} />
          {categories.length > 0 && (
            <select value={filters.category ?? ''} onChange={e => setFilters({ ...filters, category: e.target.value || null })}
              style={{ padding: '7px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', color: palette.g700 }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        {/* Active services table */}
        {active.length === 0 ? (
          <EmptyState icon={<Tag size={22} />} title="No services yet" subtitle="Add your services with pricing and duration." actionLabel="Add Service" onAction={() => setShowCreate(true)} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Name</th><th style={TH}>Category</th><th style={TH}>Duration</th>
              <th style={{ ...TH, textAlign: 'right' }}>Price</th><th style={TH}>Status</th>
            </tr></thead>
            <tbody>
              {active.map(s => (
                <tr key={s.id} onClick={() => setEditing(s)} style={{ cursor: 'pointer', transition: `background ${transition.fast}` }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{s.name}</td>
                  <td style={TD}>{s.category ? <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: palette.accentWash, color: palette.accentDeep }}>{s.category}</span> : '—'}</td>
                  <td style={TD}>{s.duration_minutes ? `${s.duration_minutes} min` : '—'}</td>
                  <td style={{ ...TD, textAlign: 'right' }}>{s.price != null ? fmt$(s.price) : '—'}</td>
                  <td style={TD}>
                    <button onClick={e => { e.stopPropagation(); void handleToggle(s); }} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: palette.greenWash, color: palette.green }}>Active</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Inactive section */}
        {inactive.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <button onClick={() => setShowInactive(!showInactive)} style={{ fontSize: 12, fontWeight: 600, color: palette.g400, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0' }}>
              {showInactive ? '▼' : '▶'} Inactive ({inactive.length})
            </button>
            {showInactive && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <tbody>
                  {inactive.map(s => (
                    <tr key={s.id} onClick={() => setEditing(s)} style={{ cursor: 'pointer', opacity: 0.6 }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                      onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}>
                      <td style={{ ...TD, fontWeight: 500, color: palette.g500 }}>{s.name}</td>
                      <td style={TD}>{s.category ?? '—'}</td>
                      <td style={TD}>{s.duration_minutes ? `${s.duration_minutes} min` : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{s.price != null ? fmt$(s.price) : '—'}</td>
                      <td style={TD}>
                        <button onClick={e => { e.stopPropagation(); void handleToggle(s); }} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: palette.g100, color: palette.g500 }}>Inactive</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <ServicePanel isOpen={showCreate || !!editing} service={editing}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSave={async (data) => {
          if (!user) return;
          if (editing) {
            const ok = await updateService(editing.id, data, user.id);
            if (ok) { setToast({ msg: 'Service updated', type: 'success' }); setEditing(null); }
          } else {
            const id = await createService(data, user.id);
            if (id) { setToast({ msg: 'Service created', type: 'success' }); setShowCreate(false); }
          }
        }}
      />
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// ── Service Panel ──
const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

interface PanelProps {
  isOpen: boolean;
  service: Service | null;
  onClose: () => void;
  onSave: (data: Partial<Service>) => void;
}

function ServicePanel({ isOpen, service, onClose, onSave }: PanelProps) {
  const business = useAuthStore(s => s.business);
  const bizDynamic = business?.enable_dynamic_pricing === true;
  const tier = business?.tier ?? 'free';
  const canUseDynamic = tier === 'pro' || tier === 'business' || tier === 'trial';
  const canUseFillRate = tier === 'business' || tier === 'trial';

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('');
  const [duration, setDuration] = useState<number | ''>('');
  const [price, setPrice] = useState('');
  const [taxRate, setTaxRate] = useState('13');
  const [sortOrder, setSortOrder] = useState('0');
  const [introEnabled, setIntroEnabled] = useState(false);
  const [introPrice, setIntroPrice] = useState('');
  const [introLabel, setIntroLabel] = useState('First Visit Special');
  const [dynEnabled, setDynEnabled] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [peakHours, setPeakHours] = useState<PeakWindow[]>([]);
  const [offPeakHours, setOffPeakHours] = useState<PeakWindow[]>([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [wasGenerated, setWasGenerated] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!name.trim() || !business?.id) return;
    if (tier !== 'pro' && tier !== 'business' && tier !== 'trial') { setErr('AI generation requires Pro plan.'); return; }
    setGenerating(true); setErr(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('generate-service-description', {
        body: { serviceName: name.trim(), industryCategory: business?.industry ?? '', businessName: business.name, business_id: business.id },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.description) { setDesc(data.description); setWasGenerated(true); }
      else if (data?.error) setErr(data.error === 'rate_limited' ? 'Too many requests. Try again in a minute.' : 'Daily limit reached.');
    } catch { setErr('Could not generate description.'); } finally { setGenerating(false); }
  };

  useEffect(() => {
    if (isOpen && service) {
      setName(service.name); setDesc(service.description ?? ''); setCategory(service.category ?? '');
      setDuration(service.duration_minutes ?? '');
      setPrice(service.price != null ? canonicalMoney(service.price) : '');
      setTaxRate(service.tax_rate != null ? String(service.tax_rate) : '13');
      setSortOrder(service.sort_order != null ? String(service.sort_order) : '0');
      setIntroEnabled(service.intro_enabled ?? false);
      setIntroPrice(service.intro_price != null ? canonicalMoney(service.intro_price) : '');
      setIntroLabel(service.intro_label ?? 'First Visit Special');
      setDynEnabled(service.enable_dynamic_pricing ?? false);
      setMinPrice(service.min_price != null ? canonicalMoney(service.min_price) : '');
      setMaxPrice(service.max_price != null ? canonicalMoney(service.max_price) : '');
      setPeakHours(service.peak_hours ?? []);
      setOffPeakHours(service.off_peak_hours ?? []);
    } else if (isOpen) {
      setName(''); setDesc(''); setCategory(''); setDuration(''); setPrice('');
      setTaxRate('13'); setSortOrder('0');
      setIntroEnabled(false); setIntroPrice(''); setIntroLabel('First Visit Special');
      setDynEnabled(false); setMinPrice(''); setMaxPrice('');
      setPeakHours([]); setOffPeakHours([]);
    }
    setErr(null);
  }, [isOpen, service]);

  const handleSave = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    if (introEnabled && introPrice && price && Number(introPrice) >= Number(price)) {
      setErr('Intro price must be less than the base price'); return;
    }
    if (introEnabled && !introPrice) { setErr('Intro price is required when intro offer is enabled'); return; }
    if (dynEnabled && minPrice && price && Number(minPrice) > Number(price)) {
      setErr('Off-peak price must be less than or equal to base price'); return;
    }
    if (dynEnabled && maxPrice && price && Number(maxPrice) < Number(price)) {
      setErr('Peak price must be greater than or equal to base price'); return;
    }
    setSaving(true); setErr(null);
    onSave({
      name: name.trim(), description: desc || null, category: category || null,
      duration_minutes: duration !== '' ? Number(duration) : undefined,
      // Q1.5 boundary: canonical string → Number() at DB-insert boundary (Rule #34)
      price: price ? Number(canonicalMoney(price)) : undefined,
      tax_rate: taxRate ? Number(taxRate) : undefined,
      sort_order: sortOrder ? Number(sortOrder) : 0,
      intro_enabled: introEnabled,
      intro_price: introPrice ? Number(canonicalMoney(introPrice)) : null,
      intro_label: introLabel.trim() || 'First Visit Special',
      enable_dynamic_pricing: dynEnabled,
      min_price: minPrice ? Number(canonicalMoney(minPrice)) : null,
      max_price: maxPrice ? Number(canonicalMoney(maxPrice)) : null,
      peak_hours: peakHours,
      off_peak_hours: offPeakHours,
    });
    setSaving(false);
  };

  const F: React.CSSProperties = { marginBottom: 14 };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={service ? 'Edit Service' : 'New Service'} subtitle={service ? service.name : 'Add a service offering'}>
      {err && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{err}</div>}

      <div style={F}><label style={LABEL}>Name *</label><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Standard Haircut" /></div>
      <div style={F}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <label style={{ ...LABEL, marginBottom: 0 }}>Description</label>
          <button onClick={handleGenerate} disabled={generating || !name.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: 'none', background: palette.accentWash, fontSize: 12, fontWeight: 600, color: palette.accent, cursor: (generating || !name.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: (generating || !name.trim()) ? 0.5 : 1 }}><Sparkles size={12} /> {generating ? 'Generating...' : 'Generate'}</button>
        </div>
        <textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={desc} onChange={e => { setDesc(e.target.value); setWasGenerated(false); }} />
        {wasGenerated && <div style={{ fontSize: 11, color: palette.g400, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}><Sparkles size={10} /> AI-generated</div>}
      </div>
      <div style={F}><label style={LABEL}>Category</label><input style={INPUT} value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Cuts, Colour, Plumbing" /></div>

      {/* Duration presets */}
      <div style={F}>
        <label style={LABEL}>Duration (minutes)</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          {DURATION_PRESETS.map(d => (
            <button key={d} onClick={() => setDuration(d)} style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
              border: duration === d ? `2px solid ${palette.accent}` : `1px solid ${palette.g200}`,
              background: duration === d ? palette.accentWash : palette.white,
              color: duration === d ? palette.accentDeep : palette.g600,
            }}>{d}m</button>
          ))}
        </div>
        <input type="number" style={{ ...INPUT, width: 120 }} value={duration} onChange={e => setDuration(e.target.value ? Number(e.target.value) : '')} placeholder="Custom" />
      </div>

      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL}>Price</label>
          <MoneyInput value={price} onChange={setPrice} ariaLabel="Price" />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Tax rate (%)</label><input type="number" style={INPUT} value={taxRate} onChange={e => setTaxRate(e.target.value)} step="0.5" /></div>
      </div>

      {/* Intro Offer */}
      <div style={{ ...F, padding: 16, background: introEnabled ? '#FFF8F0' : palette.g50, borderRadius: 10, border: `1px solid ${introEnabled ? '#F59E0B30' : palette.g150}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: introEnabled ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.g800 }}>Intro offer</div>
            <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>Special price for first-time clients</div>
          </div>
          <button onClick={() => setIntroEnabled(!introEnabled)} style={{
            width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
            background: introEnabled ? '#F59E0B' : palette.g200, position: 'relative', transition: 'background 0.2s',
          }}>
            <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 2, left: introEnabled ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
          </button>
        </div>
        {introEnabled && (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Intro price *</label>
                <MoneyInput value={introPrice} onChange={setIntroPrice} ariaLabel="Intro price" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Label (max 40 chars)</label>
                <input style={INPUT} value={introLabel} onChange={e => setIntroLabel(e.target.value.slice(0, 40))} placeholder="First Visit Special" />
              </div>
            </div>
            {/* Preview */}
            {introPrice && price && (
              <div style={{ padding: 12, background: palette.white, borderRadius: 8, border: `1px solid ${palette.g200}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#FFF1D6', color: '#92400E' }}>{introLabel}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>${introPrice}</span>
                <span style={{ fontSize: 13, color: palette.g400, textDecoration: 'line-through' }}>${price}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: palette.green }}>Save ${Number(subtractMoney(canonicalMoney(price), canonicalMoney(introPrice))).toFixed(0)}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dynamic Pricing */}
      {canUseDynamic && bizDynamic && (
        <div style={{ ...F, padding: 16, background: palette.g50, borderRadius: 10, border: `1px solid ${palette.g150}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: dynEnabled ? 14 : 0 }}>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.g800 }}>Dynamic pricing</div>
              <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>Adjust price based on demand</div>
            </div>
            <button onClick={() => setDynEnabled(!dynEnabled)} style={{
              width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
              background: dynEnabled ? palette.accent : palette.g200, position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 2, left: dynEnabled ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
            </button>
          </div>

          {dynEnabled && (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={LABEL}>Off-peak price</label>
                  <MoneyInput value={minPrice} onChange={setMinPrice} placeholder={price || '0.00'} ariaLabel="Off-peak price" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={LABEL}>Peak price</label>
                  <MoneyInput value={maxPrice} onChange={setMaxPrice} placeholder={price || '0.00'} ariaLabel="Peak price" />
                </div>
              </div>

              {/* Visual range bar */}
              {minPrice && maxPrice && price && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ height: 6, borderRadius: 3, background: palette.g200, position: 'relative' }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, borderRadius: 3, background: `linear-gradient(90deg, ${palette.green}40, ${palette.accent}40, ${palette.red}40)` }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: palette.g400, marginTop: 4 }}>
                    <span>${minPrice}</span>
                    <span style={{ color: palette.g600, fontWeight: 600 }}>${price}</span>
                    <span>${maxPrice}</span>
                  </div>
                </div>
              )}

              {/* Peak hours */}
              <TimeWindowEditor label="Peak hours" sublabel="When to charge peak price" windows={peakHours} onChange={setPeakHours} />

              {/* Off-peak hours */}
              <TimeWindowEditor label="Off-peak hours" sublabel="When to charge off-peak price" windows={offPeakHours} onChange={setOffPeakHours} />

              <div style={{ fontSize: 11, color: palette.g400, marginTop: 8, lineHeight: 1.5 }}>
                {canUseFillRate
                  ? 'Leave peak and off-peak empty to let CrewBit adjust prices automatically based on how busy each time slot is.'
                  : 'Define peak and off-peak hours manually. Automatic fill-rate pricing is available on the Business plan.'}
              </div>
            </>
          )}
        </div>
      )}

      <div style={F}><label style={LABEL}>Sort order</label><input type="number" style={{ ...INPUT, width: 80 }} value={sortOrder} onChange={e => setSortOrder(e.target.value)} /></div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : service ? 'Update' : 'Create'}</button>
      </div>
    </SlidePanel>
  );
}
